/* storybook-check-ignore */
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { Text, Box, Flex, Link } from '@opendoor/bricks/core';
import SeoHead from '../../components/articles/seo';
import { useInView } from 'react-intersection-observer';
import typography from '../../components/articles/typography';
import { CustomGlobalStyles } from '../../components/shared/GlobalStyles';
import Header from '../../components/shared/Header';
import MenuPanel, { MenuOverlay } from '../../components/shared/MenuPanel';
import { useState } from 'react';
import { colors } from '@opendoor/bricks/theme/eero/foundations';
import SpotlightArticles from '../../components/articles/SpotlightArticles';
import { PersonInline, PersonWithBio } from '../../components/articles/Person';
import Footer from '../../components/shared/Footer';
import { COSMOS_URL } from '../../components/globals';
import { ArticleProps, fetchArticleBySlug } from '../../cms';
import RichTextRenderer from '../../cms/renderer';
import articleEntryLookup from '../../cms/entries/article';
import { Reset } from '@opendoor/bricks/styles/Reset';

const MAX_WIDTH = 1440;
const HEADER_HEIGHT = 65;

// subset of the full GlobalStyles css, mostly just needed so we can use
// the shared Header, ideally that would be rewritten to use bricks components
// and then we would need this
// resetting the margin/padding is also important so we can use proper
// percentages for the sticky sidebar without a visual shift
const customGlobalCss = `
* {
  box-sizing: border-box;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
}
body {
  margin: 0 auto;
  padding: 0;
  font: 400 16px/1.7 Graphik, sans-serif;
}

input,
button {
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

a {
  color: ${colors.brand50};
  text-decoration: none;
  font: 400 16px/1.7 Graphik, sans-serif;
}
`;

const Article: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const [ref, inView] = useInView();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen((val) => !val);

  // we're "inView" on the server-side
  const inViewSSR = typeof window === 'undefined' ? true : inView;
  return (
    <>
      <Reset />
      <CustomGlobalStyles customCss={customGlobalCss} />
      <SeoHead
        og={props.fields.openGraph}
        ogArticle={props.fields.openGraphArticle}
        base={props.fields.seoBase}
        authors={props.fields.authors}
        twitterCard={props.fields.twitterCard}
        sys={props.sys}
        publisher={props.fields.publisher}
        publishDate={props.fields.publishDate}
      />
      <MenuPanel active={isMenuOpen} toggleMenuOpen={toggleMenu} />
      <MenuOverlay active={isMenuOpen} />
      <Header isMenuOpen={isMenuOpen} toggleMenuOpen={toggleMenu} />
      <main tabIndex={-1}>
        <Box
          as="article"
          backgroundColor="neutrals0"
          position={'relative'}
          width="100%"
          maxWidth={[null, null, null, null, `${MAX_WIDTH}px`]}
          // extra large screens, center the content
          marginX={[null, null, null, null, 'auto']}
          marginTop={`${HEADER_HEIGHT}px`}
        >
          <Flex
            justifyContent={'space-around'}
            flexDirection={'column'}
            mx={4}
            my={4}
            paddingLeft={[6, null, null, '12']}
            paddingRight={[6, null, null, '20%']}
            paddingBottom={[4, 6, 6, 10]}
            borderRadius="roundedSmooth"
            borderWidth="thin"
            borderColor="neutrals20"
            backgroundColor="neutrals10"
            ref={ref}
          >
            <Text marginBottom="6" {...typography.title} color="neutrals100" as="h1">
              {props.fields.articleName}
            </Text>
            <Text size="s3" {...typography.headerSummary} marginTop="0" mb={[3, 3, 4, 6]}>
              {props.fields.headerSummary}
            </Text>
            {/* Only use the primary author in the header */}
            <PersonInline person={props.fields.authors?.[0]}>
              <Text size="s0" color="neutrals90" marginTop="0">
                {props.fields.publishDate && (
                  <>{new Date(props.fields.publishDate).toLocaleDateString('en-us')} &middot; </>
                )}
                {props.readingTimeMinutes && `${props.readingTimeMinutes} min read`}
              </Text>
            </PersonInline>
          </Flex>
          <Flex
            flexDirection={'column'}
            paddingLeft={12}
            marginBottom={6}
            display={['none', null, null, 'inherit']}
            borderRight={'2px solid #f2f2f2'}
            alignItems="flex-start"
            width="25%"
            maxWidth={`${MAX_WIDTH * 0.25}px`}
            height="100%"
            position={[null, null, null, inViewSSR ? 'absolute' : 'fixed']}
            {...{ top: [null, null, null, inViewSSR ? null : `${HEADER_HEIGHT}px`] }}
          >
            <Box borderBottom={'2px solid #f2f2f2'} width="100%" paddingRight="5" paddingBottom={8}>
              <Text marginBottom={0} my="4" fontWeight="semibold" size="s2">
                Curious what your home is worth?
              </Text>
              <Text mb="4">Get an offer in minutes, sell in a matter of days.</Text>
              <Link
                href={COSMOS_URL}
                variant="primary-button"
                aria-label={null}
                marginLeft={0}
                size="sm"
                width={['100%', null, null, null, '80%']}
                analyticsName="cosmos-join-opendoor-email-list"
              >
                Find out
              </Link>
            </Box>
            <Box width="100%" paddingRight={4} paddingBottom={8}>
              <SpotlightArticles sectionTitle="Spotlight" articles={props.fields.relatedArticles} />
            </Box>
          </Flex>
          <Box
            marginLeft={[7, null, null, '30%']}
            marginRight={[7, null, null, '30%']}
            width={[null, null, null, '50%']}
            overflow={['hidden', null, null, 'visible']}
          >
            <Box>
              <RichTextRenderer.Component
                body={props.fields.keyTakeaways!}
                idToLoaderData={props.idToLoaderData}
                entryLookup={articleEntryLookup}
              />
            </Box>
            <Box marginBottom={10}>
              <RichTextRenderer.Component
                body={props.fields.body}
                idToLoaderData={props.idToLoaderData}
                entryLookup={articleEntryLookup}
              />
            </Box>
            <Box borderTop={'2px solid #f2f2f2'} paddingTop={10}>
              {(props.fields.authors || []).map((author, i) => {
                return <PersonWithBio roleText="Author" key={`author-${i}`} person={author} />;
              })}
              {(props.fields.editors || []).map((editor, i) => {
                return <PersonWithBio roleText="Editor" key={`editor-${i}`} person={editor} />;
              })}
              {(props.fields.reviewers || []).map((reviewer, i) => {
                return (
                  <PersonWithBio roleText="Reviewer" key={`reviewer-${i}`} person={reviewer} />
                );
              })}
            </Box>
            <Box
              display={['inherit', null, null, 'none']}
              borderTop={'2px solid #f2f2f2'}
              paddingTop={8}
            >
              <SpotlightArticles
                sectionTitle="Related Articles"
                articles={props.fields.relatedArticles}
              />
            </Box>
            <Footer />
          </Box>
        </Box>
      </main>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<ArticleProps, { name: Array<string> }> = async (
  context,
) => {
  if (!context.params) {
    return {
      notFound: true,
    };
  }
  try {
    const props = await fetchArticleBySlug(`/articles/${context.params.name.join('/')}`);
    if (!props) {
      return {
        notFound: true,
      };
    }
    return {
      props,
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
};

export default Article;
